// Generic list/editor shell for the Analytics tab's object sections
// (Business Areas / Schema Design / Visuals / Dashboards). Two modes:
//   • LIST — a full-width responsive CARD GRID (chip + name + description +
//     meta pills + Edit + a ⋮ menu carrying View / Where it's used / Delete)
//     with a big section header + "+ New …" action.
//   • EDITOR — the list is hidden and the EXISTING full designer for the
//     picked object fills the whole width (via EmbeddedDesignerProps), under
//     a "← {heading}" back bar.
// Includes the same unsaved-changes guard pattern the Prototype tab uses
// (dirty flag + registered save fn + Save & Continue / Discard / Keep Editing
// modal) and a small "New …" dialog that calls the section's generate
// endpoint.
import { useCallback, useEffect, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { ArrowLeft, Eye, Link2, MoreVertical, Plus, Search, Trash2 } from 'lucide-react'
import Modal from '@/components/common/Modal'
import DiscardChangesModal from '@/components/common/DiscardChangesModal'
import type { EmbeddedDesignerProps } from '@/pages/designers/embeddedDesigner'
import type { DependableType } from '@/api/designers'
import DeleteObjectDialog from './DeleteObjectDialog'
import WhereUsedDialog from './WhereUsedDialog'
import { cardStyle, InlineError, MicroBadge, TypeChip } from './shared'

/** One entry in a card's ⋮ menu. */
interface CardMenuItem {
  key: string
  label: string
  icon: ReactNode
  danger?: boolean
  onSelect: () => void
}

/** The card's overflow (⋮) menu — holds every card action except Edit, so
    the name + object name keep the width they need. Closes on outside click,
    Escape and after a pick; every handler stops propagation so the card
    underneath never opens the designer. */
function CardMenu({ items, label }: { items: CardMenuItem[]; label: string }) {
  const [open, setOpen] = useState(false)
  const wrapRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  if (items.length === 0) return null

  return (
    <div ref={wrapRef} style={{ position: 'relative', flexShrink: 0 }} onClick={(e) => e.stopPropagation()}>
      <button
        className="btn btn-secondary btn-sm"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`More actions for ${label}`}
        title="More actions"
        style={{ padding: '4px 5px', display: 'inline-flex', alignItems: 'center' }}
        onClick={(e) => { e.stopPropagation(); setOpen((v) => !v) }}
      >
        <MoreVertical size={15} />
      </button>
      {open && (
        <div
          role="menu"
          style={{
            position: 'absolute', top: 'calc(100% + 4px)', right: 0,
            minWidth: 190, padding: 4, zIndex: 30,
            background: 'var(--color-bg)',
            border: '1px solid var(--color-border)',
            borderRadius: 8,
            boxShadow: '0 8px 24px rgba(0,0,0,0.18)',
          }}
        >
          {items.map((it) => (
            <button
              key={it.key}
              role="menuitem"
              onClick={(e) => { e.stopPropagation(); setOpen(false); it.onSelect() }}
              style={{
                display: 'flex', alignItems: 'center', gap: 8, width: '100%',
                padding: '7px 9px', border: 'none', borderRadius: 6,
                background: 'transparent', cursor: 'pointer',
                fontSize: 14.5, textAlign: 'left', whiteSpace: 'nowrap',
                color: it.danger ? 'var(--color-error)' : 'var(--color-text)',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--color-bg-secondary)' }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent' }}
            >
              {it.icon}
              {it.label}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

export interface MasterItem {
  id: string
  name: string
  meta?: string
  /** Small status marker on the card's second action line, under Edit. When
      `icon` is set it renders icon-only (with `text` as the tooltip) — the
      compact treatment — otherwise it falls back to the text pill. */
  badge?: { text: string; tone: 'success' | 'muted' | 'warn' | 'accent' | 'error'; icon?: ReactNode }
  /** Small icon shown right after the name (e.g. a smart-page kind icon).
      `label` is the tooltip / accessible name. */
  titleIcon?: { node: ReactNode; label: string }
  /** Mono identity line under the name (e.g. "pharma_sales_schema · visual schema"). */
  subtitle?: string
  /** Plain-language description paragraph (clamped to 3 lines on the card). */
  description?: string
  /** Muted meta pills (e.g. "10 tables · 4 joins", "updated today"). */
  tags?: string[]
}

export default function ObjectMasterDetail({
  project,
  typeCode,
  heading,
  subCopy,
  items,
  loading,
  error,
  onRetry,
  newLabel,
  newDialogTitle,
  namePlaceholder,
  descPlaceholder,
  createButtonLabel,
  onCreate,
  onCreateManual,
  manualButtonLabel,
  renderDesigner,
  emptyState,
  listHeader,
  registerLeaveGuard,
  initialObjName,
  openNonce,
  itemSelection,
  headerActions,
  detailToolbar,
  renderPreview,
  onEditorClose,
  onObjectSaved,
  onView,
  deleteConfig,
}: {
  project: string
  typeCode: string
  heading: string
  subCopy: string
  items: MasterItem[]
  loading: boolean
  error: string | null
  onRetry: () => void
  /** "+ New …" button label; omit to hide the button. */
  newLabel?: string
  newDialogTitle?: string
  namePlaceholder?: string
  descPlaceholder?: string
  createButtonLabel?: string
  /** Generate + persist the object; resolve the created item id (selects it)
      or null on failure (the handler shows its own toast). */
  onCreate?: (name: string, description: string) => Promise<string | null>
  /** Optional MANUAL create path (no AI): persist a blank object and resolve
      its id so the designer opens on it. When set, the "New …" dialog shows a
      "Create manually" button beside "Generate with AI". */
  onCreateManual?: (name: string, description: string) => Promise<string | null>
  /** Label for the manual button (default "Create manually"). */
  manualButtonLabel?: string
  renderDesigner: (objName: string, common: EmbeddedDesignerProps) => ReactNode
  /** Shown in the card area when the list is EMPTY (coaching copy). A
      function form receives an opener for the "New …" dialog. */
  emptyState?: ReactNode | ((openNew: () => void) => ReactNode)
  /** Extra content under the section header (e.g. Visuals tabs + queue). */
  listHeader?: ReactNode
  /** Lets the Analytics shell guard rail-section switches while dirty. */
  registerLeaveGuard?: (fn: ((proceed: () => void) => void) | null) => void
  /** Select this object when it appears in `items` (deep link from Overview
      / guided-build Review). `openNonce` re-triggers it. */
  initialObjName?: string
  openNonce?: number
  /** Multi-select checkboxes on the cards (e.g. Visuals › Standardise…).
      Cards whose id is in `eligible` (all cards when omitted) get a checkbox;
      toggling never opens the card. */
  itemSelection?: {
    selected: ReadonlySet<string>
    onToggle: (id: string) => void
    eligible?: ReadonlySet<string>
  }
  /** Extra action(s) in the section header, left of "+ New …"
      (e.g. the "Standardise selected" button). */
  headerActions?: ReactNode
  /** Slim action bar rendered in the editor back-bar for the open object
      (e.g. Standardise… / Add to menu…). */
  detailToolbar?: (objName: string) => ReactNode
  /** Lazy card preview (Visuals / Dashboards). Rendered inside each card's
      media area; expected to fetch-once + cache internally so re-mounts are
      instant. Omit to show the plain type-chip cards. */
  renderPreview?: (item: MasterItem) => ReactNode
  /** Fires when the editor for an object closes (back / discard) — lets a
      section bust that object's cached preview so an edit is reflected. */
  onEditorClose?: (objName: string) => void
  /** Fires when the open designer persists the object. Sections whose cards
      show server-derived metadata (a Business Area's / Schema's table count)
      re-fetch their list here — without it the count stays at whatever it was
      when the section mounted, so a freshly-filled object reads "0 tables". */
  onObjectSaved?: (objName: string) => void
  /** When provided, each card shows a "View" action (opens a live preview of
      the SAVED object) alongside Edit. It must NOT open the designer. */
  onView?: (id: string) => void
  /** When provided, each card + the editor back-bar show a Delete action that
      opens the shared dependency-checked delete dialog. */
  deleteConfig?: {
    objType: DependableType
    /** Lowercase noun for prose, e.g. "visual", "dashboard". */
    objLabel: string
    /** Performs the actual delete for the given object id. */
    doDelete: (id: string) => Promise<void>
    /** Called after a successful delete (refresh the list). */
    onDeleted: () => void
  }
}) {
  const [selected, setSelected] = useState<string | null>(null)
  const [filter, setFilter] = useState('')
  // Delete dialog target (id + friendly name), opened from a card or back-bar.
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null)
  // "Where it's used" target (⋮ menu) — the read-only dependency view.
  const [usedTarget, setUsedTarget] = useState<{ id: string; name: string } | null>(null)

  // ── Unsaved-changes guard (same pattern as PrototypeTab) ──
  const [dirty, setDirty] = useState(false)
  const [guardOpen, setGuardOpen] = useState(false)
  const [guardSaving, setGuardSaving] = useState(false)
  const saveRef = useRef<null | (() => Promise<boolean>)>(null)
  const pendingRef = useRef<null | (() => void)>(null)

  const handleDirtyChange = useCallback((d: boolean) => setDirty(d), [])
  const handleRegisterSave = useCallback((fn: () => Promise<boolean>) => {
    saveRef.current = fn
  }, [])

  // `selected` is read through a ref so the callback identity stays stable —
  // designers register it in an effect and a changing identity re-fires them.
  const selectedRef = useRef<string | null>(null)
  selectedRef.current = selected
  const savedCbRef = useRef(onObjectSaved)
  savedCbRef.current = onObjectSaved
  const handleSaved = useCallback(() => {
    savedCbRef.current?.(selectedRef.current ?? '')
  }, [])

  const guardNav = useCallback((action: () => void) => {
    if (dirty) {
      pendingRef.current = action
      setGuardOpen(true)
    } else {
      action()
    }
  }, [dirty])

  const runPending = useCallback(() => {
    const a = pendingRef.current
    pendingRef.current = null
    setDirty(false)
    if (a) a()
  }, [])

  const handleGuardSave = useCallback(async () => {
    setGuardSaving(true)
    let ok = true
    try {
      ok = saveRef.current ? await saveRef.current() : true
    } catch {
      ok = false
    } finally {
      setGuardSaving(false)
    }
    if (!ok) return // save failed → stay
    setGuardOpen(false)
    runPending()
  }, [runPending])

  // Expose the guard to the shell while dirty so rail clicks are protected.
  useEffect(() => {
    if (!registerLeaveGuard) return
    if (!dirty) {
      registerLeaveGuard(null)
      return
    }
    registerLeaveGuard((proceed: () => void) => {
      pendingRef.current = proceed
      setGuardOpen(true)
    })
    return () => registerLeaveGuard(null)
  }, [dirty, registerLeaveGuard])

  // Deep-link selection (Overview cards / guided-build Review rows).
  useEffect(() => {
    if (initialObjName && items.some((i) => i.id === initialObjName)) {
      setSelected(initialObjName)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialObjName, openNonce, items.length])

  // Drop a selection that no longer exists (deleted elsewhere).
  useEffect(() => {
    if (selected && !loading && items.length > 0 && !items.some((i) => i.id === selected)) {
      setSelected(null)
    }
  }, [items, loading, selected])

  // Fire onEditorClose when the editor closes (back / discard) so a section
  // can bust the just-edited object's cached preview.
  const prevSelectedRef = useRef<string | null>(null)
  useEffect(() => {
    const prev = prevSelectedRef.current
    if (prev && !selected) onEditorClose?.(prev)
    prevSelectedRef.current = selected
  }, [selected, onEditorClose])

  // ── New-object dialog ──
  const [newOpen, setNewOpen] = useState(false)
  const [newName, setNewName] = useState('')
  const [newDesc, setNewDesc] = useState('')
  const [creating, setCreating] = useState(false)
  // Which button is in-flight — drives the busy label + the "Generating with
  // AI…" hint (shown for 'ai' only; manual create is instant).
  const [createMode, setCreateMode] = useState<'ai' | 'manual' | null>(null)

  const runCreate = async (mode: 'ai' | 'manual') => {
    const fn = mode === 'manual' ? onCreateManual : onCreate
    if (!fn || !newName.trim() || creating) return
    setCreateMode(mode)
    setCreating(true)
    try {
      const created = await fn(newName.trim(), newDesc.trim())
      if (created) {
        setNewOpen(false)
        setNewName('')
        setNewDesc('')
        setSelected(created)
      }
    } finally {
      setCreating(false)
      setCreateMode(null)
    }
  }

  const visible = filter.trim()
    ? items.filter((i) =>
        (i.name + ' ' + i.id + ' ' + (i.meta || '') + ' ' + (i.description || '') + ' ' + (i.subtitle || ''))
          .toLowerCase()
          .includes(filter.trim().toLowerCase()))
    : items

  const selectedItem = selected ? items.find((i) => i.id === selected) : null

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '8px 10px',
    fontSize: 15,
    border: '1px solid var(--color-border-strong)',
    borderRadius: 7,
    background: 'var(--color-input-bg)',
    color: 'var(--color-text)',
  }

  // Dependency-checked delete dialog, shared by list cards + the editor
  // back-bar. Rendered in both modes below.
  // "Where it's used" reuses the delete dialog's dependency check, so it needs
  // the same DependableType — i.e. it rides along with deleteConfig.
  const whereUsedDialog = deleteConfig && usedTarget ? (
    <WhereUsedDialog
      open
      project={project}
      objType={deleteConfig.objType}
      objName={usedTarget.id}
      displayName={usedTarget.name}
      objLabel={deleteConfig.objLabel}
      onClose={() => setUsedTarget(null)}
    />
  ) : null

  const deleteDialog = deleteConfig && deleteTarget ? (
    <DeleteObjectDialog
      open
      project={project}
      objType={deleteConfig.objType}
      objName={deleteTarget.id}
      displayName={deleteTarget.name}
      objLabel={deleteConfig.objLabel}
      onClose={() => setDeleteTarget(null)}
      onDeleted={() => {
        // If the deleted object was open in the editor, return to the list.
        setSelected((cur) => (cur === deleteTarget.id ? null : cur))
        deleteConfig.onDeleted()
      }}
      doDelete={() => deleteConfig.doDelete(deleteTarget.id)}
    />
  ) : null

  // ── EDITOR MODE — the picked object's full designer fills the width ──
  if (selected) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, minWidth: 0, background: 'var(--color-bg)' }}>
        <div
          style={{
            display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap',
            padding: '12px 20px', borderBottom: '1px solid var(--color-border)',
            background: 'var(--color-bg-secondary)', flexShrink: 0,
          }}
        >
          <button
            className="btn btn-secondary btn-sm"
            style={{ whiteSpace: 'nowrap' }}
            onClick={() => guardNav(() => setSelected(null))}
          >
            <ArrowLeft size={15} />
            {heading}
          </button>
          <span style={{ fontSize: 14.5, color: 'var(--color-text-secondary)' }}>
            Use the editor's <strong style={{ color: 'var(--color-text)' }}>Save</strong> button to persist your changes.
          </span>
          {(detailToolbar || deleteConfig) && (
            <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
              {detailToolbar?.(selected)}
              {deleteConfig && (
                <button
                  className="btn btn-secondary btn-sm"
                  style={{ color: 'var(--color-error)', whiteSpace: 'nowrap' }}
                  title={`Delete this ${deleteConfig.objLabel}`}
                  onClick={() => setDeleteTarget({ id: selected, name: selectedItem?.name || selected })}
                >
                  <Trash2 size={13} /> Delete
                </button>
              )}
            </div>
          )}
        </div>
        {/* Designer wrapper: overflow AUTO, not hidden. Some embedded
            designers (Schema / Visual / Dashboard) size themselves to
            height:100% and scroll internally — they never overflow this box.
            Others (Business Area) render at their natural content height and
            rely on an ancestor scroll container; `auto` makes this wrapper
            that scroll container while staying inert for self-scrolling ones. */}
        <div style={{ flex: 1, minHeight: 0, overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
          {renderDesigner(selected, {
            embedded: true,
            embedProject: project,
            embedObjName: selectedItem?.id ?? selected,
            onDirtyChange: handleDirtyChange,
            registerSave: handleRegisterSave,
            onSaved: handleSaved,
          })}
        </div>

        {/* ── Unsaved-changes guard ── */}
        <DiscardChangesModal
          open={guardOpen}
          saving={guardSaving}
          message="The designer has unsaved changes. Save them before leaving, or discard them and continue?"
          onCancel={() => { setGuardOpen(false); pendingRef.current = null }}
          onDiscard={() => { setGuardOpen(false); runPending() }}
          onSave={() => void handleGuardSave()}
        />
        {deleteDialog}
        {whereUsedDialog}
      </div>
    )
  }

  // ── LIST MODE — full-width card grid ──
  return (
    <div style={{ flex: 1, minHeight: 0, minWidth: 0, overflowY: 'auto', background: 'var(--color-bg)' }}>
      <div style={{ padding: '22px 28px 44px', display: 'flex', flexDirection: 'column', gap: 14, maxWidth: 1240, margin: '0 auto' }}>
        {/* Section header */}
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
          <TypeChip code={typeCode} size={34} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <h2 style={{ margin: 0, fontSize: 26, fontWeight: 700, lineHeight: 1.15 }}>{heading}</h2>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
            {headerActions}
            {newLabel && onCreate && (
              <button
                className="btn btn-primary"
                style={{ whiteSpace: 'nowrap' }}
                onClick={() => setNewOpen(true)}
              >
                <Plus size={15} />
                {newLabel}
              </button>
            )}
          </div>
        </div>
        <p style={{ margin: 0, fontSize: 15.5, lineHeight: 1.55, color: 'var(--color-text-secondary)', maxWidth: 760 }}>
          {subCopy}
        </p>
        {listHeader}
        {items.length > 6 && (
          <div style={{ position: 'relative', maxWidth: 360 }}>
            <Search
              size={14}
              style={{
                position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)',
                color: 'var(--color-text-secondary)',
              }}
            />
            <input
              style={{ ...inputStyle, paddingLeft: 30 }}
              placeholder="Filter…"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
            />
          </div>
        )}

        {/* Card grid / states */}
        {loading && items.length === 0 ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: 16 }}>
            {[0, 1, 2, 3].map((i) => (
              <div
                key={i}
                style={{
                  height: 150, borderRadius: 12,
                  background: 'var(--color-bg-secondary)',
                  border: '1px solid var(--color-border)',
                  opacity: 0.6,
                }}
              />
            ))}
          </div>
        ) : error ? (
          <InlineError message={error} onRetry={onRetry} />
        ) : items.length === 0 ? (
          typeof emptyState === 'function' ? emptyState(() => setNewOpen(true)) : emptyState
        ) : visible.length === 0 ? (
          <p style={{ fontSize: 15.5, color: 'var(--color-text-secondary)', padding: '8px 2px' }}>
            No matches.
          </p>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: 16, alignItems: 'start' }}>
            {visible.map((item) => {
              const checkable = itemSelection
                && (!itemSelection.eligible || itemSelection.eligible.has(item.id))
              const isChecked = !!itemSelection && itemSelection.selected.has(item.id)
              const open = () => guardNav(() => setSelected(item.id))
              return (
                <div
                  key={item.id}
                  onClick={open}
                  style={{
                    ...cardStyle,
                    padding: 16,
                    cursor: 'pointer',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 12,
                    minWidth: 0,
                    outline: isChecked ? '2px solid var(--color-accent)' : 'none',
                    outlineOffset: -1,
                    transition: 'border-color .12s ease',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--color-accent)' }}
                  onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--color-border)' }}
                >
                  {/* Preview media (Visuals / Dashboards) */}
                  {renderPreview && (
                    <div
                      style={{
                        height: 132, borderRadius: 9, overflow: 'hidden',
                        border: '1px solid var(--color-border)',
                        background: 'var(--color-surface-2)',
                        position: 'relative',
                      }}
                    >
                      {renderPreview(item)}
                    </div>
                  )}

                  {/* Header row */}
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, minWidth: 0 }}>
                    {checkable && (
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onClick={(e) => e.stopPropagation()}
                        onChange={() => itemSelection!.onToggle(item.id)}
                        style={{ cursor: 'pointer', flexShrink: 0, margin: '3px 0 0' }}
                        aria-label={`Select ${item.name}`}
                      />
                    )}
                    {!renderPreview && <TypeChip code={typeCode} size={30} />}
                    {/* Name + object name get the width: the only inline
                        action is Edit, everything else lives in the ⋮ menu
                        and the status badge drops to the line below it. */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6, minWidth: 0 }}>
                        <div
                          style={{
                            flex: 1, minWidth: 0,
                            fontWeight: 700, fontSize: 16.5, lineHeight: 1.3, color: 'var(--color-text)',
                            overflow: 'hidden', display: '-webkit-box',
                            WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                            overflowWrap: 'anywhere',
                          }}
                          title={item.name}
                        >
                          {item.name}
                        </div>
                        {item.titleIcon && (
                          <span
                            title={item.titleIcon.label}
                            aria-label={item.titleIcon.label}
                            style={{
                              flexShrink: 0, display: 'inline-flex', alignItems: 'center',
                              color: 'var(--color-text-secondary)',
                            }}
                          >
                            {item.titleIcon.node}
                          </span>
                        )}
                      </div>
                      {item.subtitle && (
                        <div
                          style={{
                            fontFamily: 'var(--font-mono, ui-monospace, monospace)',
                            fontSize: 13.5, color: 'var(--color-text-secondary)', marginTop: 3,
                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                          }}
                          title={item.subtitle}
                        >
                          {item.subtitle}
                        </div>
                      )}
                    </div>
                    {/* Edit + ⋮ on the first line, status badge on the second. */}
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6, flexShrink: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <button
                          className="btn btn-secondary btn-sm"
                          onClick={(e) => { e.stopPropagation(); open() }}
                        >
                          Edit
                        </button>
                        <CardMenu
                          label={item.name}
                          items={[
                            ...(onView ? [{
                              key: 'view',
                              label: 'View',
                              icon: <Eye size={14} />,
                              onSelect: () => onView(item.id),
                            }] : []),
                            ...(deleteConfig ? [{
                              key: 'where-used',
                              label: 'Where it’s used',
                              icon: <Link2 size={14} />,
                              onSelect: () => setUsedTarget({ id: item.id, name: item.name }),
                            }, {
                              key: 'delete',
                              label: 'Delete',
                              icon: <Trash2 size={14} />,
                              danger: true,
                              onSelect: () => setDeleteTarget({ id: item.id, name: item.name }),
                            }] : []),
                          ]}
                        />
                      </div>
                      {item.badge && (item.badge.icon
                        ? (
                          <span
                            title={item.badge.text}
                            aria-label={item.badge.text}
                            style={{ display: 'inline-flex', alignItems: 'center' }}
                          >
                            {item.badge.icon}
                          </span>
                        )
                        : <MicroBadge text={item.badge.text} tone={item.badge.tone} />)}
                    </div>
                  </div>

                  {/* Description */}
                  {(item.description || item.meta) && (
                    <p
                      style={{
                        margin: 0, fontSize: 14.5, lineHeight: 1.5, color: 'var(--color-text-secondary)',
                        overflow: 'hidden', display: '-webkit-box',
                        WebkitLineClamp: 3, WebkitBoxOrient: 'vertical',
                      }}
                    >
                      {item.description || item.meta}
                    </p>
                  )}

                  {/* Meta pills — sit directly under the content (no bottom
                      gap); the card grid uses align-items:start so each card is
                      only as tall as its own content. */}
                  {item.tags && item.tags.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                      {item.tags.map((t, i) => (
                        <MicroBadge key={i} text={t} tone="muted" />
                      ))}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* ── New-object dialog ── */}
      <Modal
        open={newOpen}
        onClose={() => { if (!creating) setNewOpen(false) }}
        title={newDialogTitle || newLabel || 'New'}
        footer={
          <>
            <button className="btn btn-secondary" onClick={() => setNewOpen(false)} disabled={creating}>
              Cancel
            </button>
            {onCreateManual && (
              <button
                className="btn btn-secondary"
                onClick={() => void runCreate('manual')}
                disabled={creating || !newName.trim()}
                title="Create a blank object and build it yourself — no AI"
              >
                {creating && createMode === 'manual' ? 'Creating…' : (manualButtonLabel || 'Create manually')}
              </button>
            )}
            <button
              className="btn btn-primary"
              onClick={() => void runCreate('ai')}
              disabled={creating || !newName.trim()}
            >
              {creating && createMode === 'ai' ? 'Generating…' : (createButtonLabel || 'Create')}
            </button>
          </>
        }
      >
        <div style={{ display: 'grid', gap: 12 }}>
          <div>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 4 }}>
              Name
            </label>
            <input
              style={inputStyle}
              value={newName}
              placeholder={namePlaceholder || 'e.g. Sales'}
              autoFocus
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') void runCreate('ai') }}
            />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 4 }}>
              Description
              <span style={{ fontWeight: 400, color: 'var(--color-text-secondary)' }}>
                {' '}— what it covers, in plain language (the AI drafts from this)
              </span>
            </label>
            <textarea
              style={{ ...inputStyle, resize: 'vertical' }}
              rows={3}
              value={newDesc}
              placeholder={descPlaceholder || 'Describe what this should contain…'}
              onChange={(e) => setNewDesc(e.target.value)}
            />
          </div>
          {creating && createMode === 'ai' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14.5, color: 'var(--color-text-secondary)' }}>
              <div className="spinner" style={{ width: 14, height: 14 }} />
              Generating with AI — this can take up to a minute…
            </div>
          )}
        </div>
      </Modal>

      {/* ── Unsaved-changes guard ── */}
      <DiscardChangesModal
        open={guardOpen}
        saving={guardSaving}
        message="The designer has unsaved changes. Save them before leaving, or discard them and continue?"
        onCancel={() => { setGuardOpen(false); pendingRef.current = null }}
        onDiscard={() => { setGuardOpen(false); runPending() }}
        onSave={() => void handleGuardSave()}
      />
      {deleteDialog}
      {whereUsedDialog}
    </div>
  )
}
